import React, { useState, useEffect, useCallback } from 'react';
import { Button, Form, Spinner, Table, Modal } from 'react-bootstrap';
import { Helmet } from 'react-helmet';
import NavContainer from '../../components/Nav';
import OffCanvasNav from '../../components/OffCanvasNav';
import AlertModal from '../../components/AlertModal';
import useAlertModal from '../../hooks/useAlertModal';
import { useAuth } from '../../context/AuthContext';
import { getTaxes, createTax, updateTax, deleteTax } from '../../services/taxApi';
import './TaxManagement.css';

const SCOPE_OPTIONS = [
  { value: '', label: 'Todos' },
  { value: 'compra', label: 'Compra' },
  { value: 'venta', label: 'Venta' },
  { value: 'gasto', label: 'Gasto' },
];

const extractArray = (data) => {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  if (Array.isArray(data.message)) return data.message;
  if (Array.isArray(data.data)) return data.data;
  return [];
};

const scopeLabel = (scope) => {
  const opt = SCOPE_OPTIONS.find((o) => o.value === scope);
  return opt ? opt.label : scope || '-';
};

const TaxManagement = () => {
  const { user } = useAuth();
  const { showAlert, showAlertModal, alertMessage, alertType, hideAlert, getErrorMessage } = useAlertModal();

  const [taxes, setTaxes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [filterScope, setFilterScope] = useState('');
  const [filterActive, setFilterActive] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editingTax, setEditingTax] = useState(null);
  const [form, setForm] = useState({
    description: '',
    scope: 'compra',
    rate: '',
    value: '',
    is_withholding: 0,
    valid_from: '',
    valid_to: '',
    priority: '',
  });

  const loadTaxes = useCallback(async () => {
    setLoading(true);
    const params = {};
    if (filterScope) params.scope = filterScope;
    if (filterActive) params.active = '1';
    const { ok, data } = await getTaxes(params);
    setLoading(false);
    if (ok) {
      const list = data?.message;
      if (list === 'NOT CONTENT' || list === null) {
        setTaxes([]);
      } else {
        setTaxes(extractArray(list ?? data));
      }
    } else {
      showAlert(getErrorMessage({ data }) || 'Error al cargar impuestos', 'error');
    }
  }, [filterScope, filterActive, showAlert, getErrorMessage]);

  useEffect(() => {
    loadTaxes();
  }, [loadTaxes]);

  const openCreate = () => {
    setEditingTax(null);
    setForm({
      description: '',
      scope: 'compra',
      rate: '',
      value: '',
      is_withholding: 0,
      valid_from: '',
      valid_to: '',
      priority: '',
    });
    setShowModal(true);
  };

  const openEdit = (tax) => {
    setEditingTax(tax);
    setForm({
      description: tax.description || '',
      scope: tax.scope || 'compra',
      rate: tax.rate ?? '',
      value: tax.value ?? '',
      is_withholding: tax.is_withholding ?? 0,
      valid_from: tax.valid_from ? tax.valid_from.slice(0, 10) : '',
      valid_to: tax.valid_to ? tax.valid_to.slice(0, 10) : '',
      priority: tax.priority ?? '',
    });
    setShowModal(true);
  };

  const openDelete = (tax) => {
    setEditingTax(tax);
    setShowDeleteModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.description?.trim()) {
      showAlert('La descripción es obligatoria', 'error');
      return;
    }
    if (form.rate === '' || form.rate == null || isNaN(parseFloat(form.rate))) {
      showAlert('La tasa es obligatoria y debe ser un número', 'error');
      return;
    }
    if (!user?.uid) {
      showAlert('Sesión no válida. Inicia sesión nuevamente.', 'error');
      return;
    }

    setModalLoading(true);
    try {
      const payload = {
        firebase_UID: user.uid,
        description: form.description.trim(),
        scope: form.scope,
        rate: parseFloat(form.rate),
        value: form.value !== '' && form.value != null ? parseFloat(form.value) : parseFloat(form.rate),
        is_withholding: form.is_withholding ? 1 : 0,
      };
      if (form.valid_from?.trim()) payload.valid_from = form.valid_from;
      if (form.valid_to?.trim()) payload.valid_to = form.valid_to;
      if (form.priority !== '' && form.priority != null) payload.priority = parseInt(form.priority, 10);

      const { ok, status, data } = editingTax
        ? await updateTax(editingTax.id_tax, payload)
        : await createTax(payload);

      if (ok && (status === 200 || status === 201 || data?.code === 200 || data?.code === 201)) {
        showAlert(data?.message || (editingTax ? 'Impuesto actualizado' : 'Impuesto creado'), 'success');
        setShowModal(false);
        loadTaxes();
      } else {
        showAlert(getErrorMessage({ data }) || 'Error al guardar', 'error');
      }
    } catch (err) {
      showAlert('Error inesperado', 'error');
    } finally {
      setModalLoading(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!editingTax?.id_tax || !user?.uid) return;
    setModalLoading(true);
    try {
      const { ok, status, data } = await deleteTax(editingTax.id_tax, user.uid);
      if (ok && status === 200) {
        showAlert(data?.message || 'Impuesto eliminado', 'success');
        setShowDeleteModal(false);
        setEditingTax(null);
        loadTaxes();
      } else {
        showAlert(getErrorMessage({ data }) || 'Error al eliminar', 'error');
      }
    } catch (err) {
      showAlert('Error inesperado', 'error');
    } finally {
      setModalLoading(false);
    }
  };

  const taxLabel = (t) => t?.description || `Impuesto #${t?.id_tax}`;

  return (
    <>
      <Helmet>
        <title>Administración de Impuestos - DotoniWoof</title>
      </Helmet>
      <NavContainer />
      <OffCanvasNav />

      <div className="tax-mgmt">
        <header className="tax-mgmt-header">
          <h1>Impuestos</h1>
          <p>Configura los impuestos para compras, ventas y gastos.</p>
        </header>

        <div className="tax-mgmt-card">
          <div className="tax-mgmt-filters">
            <div className="tax-mgmt-filter-group">
              <label>Ámbito</label>
              <Form.Select
                value={filterScope}
                onChange={(e) => setFilterScope(e.target.value)}
                className="tax-mgmt-select"
              >
                {SCOPE_OPTIONS.map((o) => (
                  <option key={o.value || 'all'} value={o.value}>{o.label}</option>
                ))}
              </Form.Select>
            </div>
            <Form.Check
              type="switch"
              id="filter-active"
              label="Solo vigentes"
              checked={filterActive}
              onChange={(e) => setFilterActive(e.target.checked)}
              className="tax-mgmt-switch"
            />
            <Button className="tax-mgmt-btn-add" onClick={openCreate}>
              + Nuevo impuesto
            </Button>
          </div>

          {loading ? (
            <div className="tax-mgmt-loading">
              <Spinner animation="border" />
              <span>Cargando impuestos...</span>
            </div>
          ) : (
            <div className="tax-mgmt-table-wrap">
              <Table responsive className="tax-mgmt-table">
                <thead>
                  <tr>
                    <th>Descripción</th>
                    <th>Ámbito</th>
                    <th>Tasa</th>
                    <th>Tipo</th>
                    <th>Vigencia</th>
                    <th className="text-end">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {taxes.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="text-center tax-mgmt-empty">
                        No hay impuestos configurados
                      </td>
                    </tr>
                  ) : (
                    taxes.map((t) => (
                      <tr key={t.id_tax}>
                        <td className="tax-mgmt-desc">{t.description || '-'}</td>
                        <td>
                          <span className={`tax-mgmt-badge tax-mgmt-badge-${t.scope || 'gasto'}`}>
                            {scopeLabel(t.scope)}
                          </span>
                        </td>
                        <td>{t.rate != null ? `${Number(t.rate)}%` : '-'}</td>
                        <td>{t.is_withholding ? 'Retención' : 'Impuesto'}</td>
                        <td className="tax-mgmt-dates">
                          {t.valid_from || t.valid_to
                            ? `${t.valid_from || '...'} – ${t.valid_to || '...'}`
                            : '-'}
                        </td>
                        <td className="text-end">
                          <Button
                            variant="link"
                            className="tax-mgmt-btn-action"
                            onClick={() => openEdit(t)}
                          >
                            Editar
                          </Button>
                          <Button
                            variant="link"
                            className="tax-mgmt-btn-action tax-mgmt-btn-danger"
                            onClick={() => openDelete(t)}
                          >
                            Eliminar
                          </Button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </div>
          )}
        </div>
      </div>

      <Modal show={showModal} onHide={() => setShowModal(false)} centered className="tax-mgmt-modal">
        <Modal.Header closeButton>
          <Modal.Title>{editingTax ? 'Editar impuesto' : 'Nuevo impuesto'}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Descripción *</Form.Label>
              <Form.Control
                value={form.description}
                onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                placeholder="Ej: IVA, Retención en la fuente"
                required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Ámbito *</Form.Label>
              <Form.Select
                value={form.scope}
                onChange={(e) => setForm((p) => ({ ...p, scope: e.target.value }))}
              >
                <option value="compra">Compra</option>
                <option value="venta">Venta</option>
                <option value="gasto">Gasto</option>
              </Form.Select>
            </Form.Group>
            <div className="row g-2">
              <div className="col-6">
                <Form.Group className="mb-3">
                  <Form.Label>Tasa (%) *</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    min="0"
                    value={form.rate}
                    onChange={(e) => setForm((p) => ({ ...p, rate: e.target.value }))}
                    placeholder="19"
                    required
                  />
                </Form.Group>
              </div>
              <div className="col-6">
                <Form.Group className="mb-3">
                  <Form.Label>Valor (opcional)</Form.Label>
                  <Form.Control
                    type="number"
                    step="0.01"
                    min="0"
                    value={form.value}
                    onChange={(e) => setForm((p) => ({ ...p, value: e.target.value }))}
                    placeholder="Igual a tasa"
                  />
                </Form.Group>
              </div>
            </div>
            <Form.Group className="mb-3">
              <Form.Check
                type="switch"
                id="is_withholding"
                label="Es retención (Rtefuente, ICA)"
                checked={!!form.is_withholding}
                onChange={(e) => setForm((p) => ({ ...p, is_withholding: e.target.checked ? 1 : 0 }))}
              />
            </Form.Group>
            <div className="row g-2">
              <div className="col-6">
                <Form.Group className="mb-3">
                  <Form.Label>Vigencia desde</Form.Label>
                  <Form.Control
                    type="date"
                    value={form.valid_from}
                    onChange={(e) => setForm((p) => ({ ...p, valid_from: e.target.value }))}
                  />
                </Form.Group>
              </div>
              <div className="col-6">
                <Form.Group className="mb-3">
                  <Form.Label>Vigencia hasta</Form.Label>
                  <Form.Control
                    type="date"
                    value={form.valid_to}
                    onChange={(e) => setForm((p) => ({ ...p, valid_to: e.target.value }))}
                  />
                </Form.Group>
              </div>
            </div>
            <Form.Group className="mb-3">
              <Form.Label>Prioridad (opcional)</Form.Label>
              <Form.Control
                type="number"
                min="0"
                value={form.priority}
                onChange={(e) => setForm((p) => ({ ...p, priority: e.target.value }))}
                placeholder="0"
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="outline-secondary" onClick={() => setShowModal(false)}>
              Cancelar
            </Button>
            <Button className="tax-mgmt-btn-submit" type="submit" disabled={modalLoading}>
              {modalLoading ? <Spinner animation="border" size="sm" /> : (editingTax ? 'Guardar' : 'Crear')}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Eliminar impuesto</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          ¿Eliminar <strong>{editingTax && taxLabel(editingTax)}</strong>? No podrás eliminarlo si está asociado a compras o gastos.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShowDeleteModal(false)}>
            Cancelar
          </Button>
          <Button variant="danger" onClick={handleConfirmDelete} disabled={modalLoading}>
            {modalLoading ? <Spinner animation="border" size="sm" /> : 'Eliminar'}
          </Button>
        </Modal.Footer>
      </Modal>

      <AlertModal show={showAlertModal} onHide={hideAlert} message={alertMessage} type={alertType} />
    </>
  );
};

export default TaxManagement;
